import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { setSelectedPlayRangeHandle } from 'actions/trimming';
import { MIN_PLAY_RANGE_DURATION } from 'constants/trimming';
import { toHslaString } from 'utilities/colors';

import useStateRef from 'hooks/useStateRef';
import useThemeColors from 'hooks/useThemeColors';
import { selectSelectedPlayRangeHandle } from 'selectors/trimming';

import HandleTooltip from './handle-tooltip';
import SectionHandle from './section-handle';

const clipBorderWidth = 2;
const cornerRoundPx = 6;
const handleWidth = 6;
const handleCurvePx = 4;

interface ClipSectionProps {
  clipId: string;
  startX: number;
  endX: number;
  minX: number;
  maxX: number;
  active: boolean;
  height: number;
  findSvgCoords: (evt: React.MouseEvent) => SVGPoint;
  svgXToSecs: (x: number) => number;
  secsToSvgX: (x: number) => number;
  outlineWidth?: number;
  onHandleGrab?: () => void;
  onHandleRelease?: (info: {
    id: string;
    leftX: number;
    rightX: number;
    activeHandle: 'left' | 'right';
  }) => void;
  onHandleMove?: (info: { id: string; leftX: number; rightX: number }) => void;
  viewboxWidth: number;
}

interface ClipSectionDefProps {
  clipId: string;
  startX: number;
  endX: number;
  height: number;
  outlineWidth?: number;
}

type Side = 'left' | 'right' | null;

export const ClipSectionDef = ({
  clipId,
  startX,
  endX,
  height,
  outlineWidth = clipBorderWidth,
}: ClipSectionDefProps): JSX.Element => {
  const width = endX - startX - outlineWidth;

  if (width < 0) {
    return null;
  }

  return (
    <rect
      id={clipId}
      x={startX + outlineWidth / 2}
      y={outlineWidth / 2}
      rx={cornerRoundPx}
      width={width}
      height={height}
    />
  );
};

const ClipSection = ({
  clipId,
  startX,
  endX,
  minX,
  maxX,
  active = false,
  height,
  outlineWidth = clipBorderWidth,
  findSvgCoords,
  onHandleGrab = () => {
    return;
  },
  onHandleRelease = () => {
    return;
  },
  onHandleMove = () => {
    return;
  },
  svgXToSecs,
  secsToSvgX,
  viewboxWidth,
}: ClipSectionProps): JSX.Element => {
  const colors = useThemeColors();
  const dispatch = useDispatch();

  const handleHeight = Math.max(28, height - 20);

  const activeClipColor = toHslaString(colors.primary);
  const hoverHandleColor = toHslaString(colors.primaryActive);
  const inactiveClipColor = toHslaString(colors.buttonBorder);
  const focusHandleColor = toHslaString(colors.focusRing);
  const clipColor = active ? activeClipColor : inactiveClipColor;

  const activeHandle = useSelector(selectSelectedPlayRangeHandle);
  const activeHandleRef = useRef(activeHandle);
  const setActiveHandle = side => {
    activeHandleRef.current = side;
    dispatch(setSelectedPlayRangeHandle(side));
  };

  const [mouseDown, setMouseDown] = useState(false);

  const [leftX, setLeftX, leftXRef] = useStateRef<number>(startX);
  const [rightX, setRightX, rightXRef] = useStateRef<number>(endX);

  useEffect(() => {
    setLeftX(startX);
    setRightX(endX);
  }, [startX, endX, setLeftX, setRightX]);

  const mouseUpListener = useRef<VoidFunction>();
  const mouseMoveListener = useRef<(evt: MouseEvent) => void>();

  const activateHandle = (evt: React.MouseEvent, side: Side) => {
    // If this isn't a left mouse click
    if (evt.button !== 0) {
      return;
    }

    if (onHandleGrab) {
      onHandleGrab();
    }

    setMouseDown(true);

    evt.preventDefault();
    setActiveHandle(side);

    document.body.style.setProperty('cursor', 'ew-resize', 'important');
    addEventListeners();
  };

  function deactivateHandle() {
    document.body.style.cursor = 'auto';
    removeEventListeners();
  }

  const handleMouseUp = () => {
    setMouseDown(false);
    onHandleRelease({
      leftX: leftXRef.current,
      rightX: rightXRef.current,
      id: clipId,
      activeHandle: activeHandleRef.current,
    });
    deactivateHandle();
  };

  const handleMouseMove = evt => {
    const minDistance = secsToSvgX(MIN_PLAY_RANGE_DURATION);

    let newX = findSvgCoords(evt).x;

    // Better dragging -- if we would drag further than allowed, then drag to
    // min or max.

    if (activeHandleRef.current === 'left') {
      if (newX < minX) {
        newX = minX;
      }

      if (newX >= rightXRef.current - minDistance) {
        newX = rightXRef.current - minDistance;
      }

      setLeftX(newX);
      onHandleMove({ id: clipId, leftX: newX, rightX: rightXRef.current });
    } else if (activeHandleRef.current === 'right') {
      if (newX > maxX) {
        newX = maxX;
      }

      if (newX <= leftXRef.current + minDistance) {
        newX = leftXRef.current + minDistance;
      }

      setRightX(newX);
      onHandleMove({
        id: clipId,
        leftX: leftXRef.current,
        rightX: newX,
      });
    }
  };

  function addEventListeners() {
    removeEventListeners();

    mouseUpListener.current = handleMouseUp;
    mouseMoveListener.current = handleMouseMove;
    document.addEventListener('mouseup', handleMouseUp);
    document.addEventListener('mousemove', handleMouseMove);
  }

  function removeEventListeners() {
    if (mouseUpListener.current) {
      document.removeEventListener('mouseup', mouseUpListener.current);
    }

    if (mouseMoveListener.current) {
      document.removeEventListener('mousemove', mouseMoveListener.current);
    }
  }

  const handleY = height / 2 - handleHeight / 2;

  const handleClickOutside = () => !mouseDown && setActiveHandle(null);

  return (
    <g>
      {active && (
        <>
          <SectionHandle
            side="left"
            secs={svgXToSecs(leftX)}
            active={activeHandle === 'left'}
            color={activeClipColor}
            hoverColor={hoverHandleColor}
            focusRingColor={focusHandleColor}
            curvePx={handleCurvePx}
            x={leftX}
            y={handleY}
            width={handleWidth}
            height={handleHeight}
            onMouseDown={(evt: React.MouseEvent) => activateHandle(evt, 'left')}
            viewboxWidth={viewboxWidth}
          />
          <SectionHandle
            side="right"
            secs={svgXToSecs(rightX)}
            active={activeHandle === 'right'}
            color={activeClipColor}
            hoverColor={hoverHandleColor}
            focusRingColor={focusHandleColor}
            curvePx={handleCurvePx}
            x={rightX}
            y={handleY}
            width={handleWidth}
            height={handleHeight}
            onMouseDown={(evt: React.MouseEvent) =>
              activateHandle(evt, 'right')
            }
            viewboxWidth={viewboxWidth}
          />
          {activeHandle && (
            <HandleTooltip
              onClickOutside={handleClickOutside}
              x={activeHandle === 'left' ? leftX : rightX}
              y={handleY + handleHeight}
              maxX={viewboxWidth}
              secs={svgXToSecs(activeHandle === 'left' ? leftX : rightX)}
              mouseDown={mouseDown}
            />
          )}
        </>
      )}

      <rect
        id={'r' + clipId}
        x={leftX + outlineWidth / 2}
        y={outlineWidth / 2}
        rx={cornerRoundPx}
        width={Math.max(0, rightX - leftX - outlineWidth)}
        height={height}
        fill="none"
        strokeWidth={outlineWidth}
        stroke={clipColor}
      />
    </g>
  );
};

export default ClipSection;
